// ─── Zone Manager v3 ──────────────────────────────────────────────────────────
// Manages multi-bay zones with mixed roof types (R-Blade, R-Shade, R-Breeze).
// Supports zones stacking along either length (Z-axis) or width (X-axis).
// Pure logic module — no 3D or DOM dependencies.

export const ROOF_TYPES = {
  'r-blade': { label: 'R-Blade', description: 'Adjustable aluminum louver blades', hasBlades: true },
  'r-shade': { label: 'R-Shade', description: 'Retractable fabric shade', hasBlades: false },
  'r-breeze': { label: 'R-Breeze', description: 'Fixed screen panels', hasBlades: false },
};

export const LAYOUT_MODES = {
  linear_length: { label: 'Linear (Length)', description: 'Zones stacked along length (Z-axis)' },
  linear_width:  { label: 'Linear (Width)', description: 'Zones stacked along width (X-axis)' },
  L_shape:       { label: 'L-Shape', description: 'Two zones forming an L-shape' },
  rectangle:     { label: 'Rectangle', description: 'Zones forming a rectangular arrangement' },
  square:        { label: 'Square', description: 'Equal-dimension zones in a square' },
};

export const CONNECTION_MODES = {
  length_to_length: { label: 'Length-to-Length', description: 'Zones share a width edge' },
  width_to_width:   { label: 'Width-to-Width', description: 'Zones share a length edge' },
  mixed:            { label: 'Mixed', description: 'Zones connect on different axes' },
};

const DEFAULT_ZONE = {
  roofType: 'r-blade',
  lengthInches: 276,     // 23'
  widthInches: 192,      // 16'
};

const MAX_ZONES = 6;
const MIN_ZONE_DIM = 72;     // 6' min for either width or length per zone
const MAX_ZONE_DIM = 276;    // 23' max per zone

let nextId = 1;

export class ZoneManager {
  constructor() {
    this.zones = [];
    this.sharedWidthInches = 192;   // 16' — shared when stacking along length
    this.sharedLengthInches = 276;  // 23' — shared when stacking along width
    this.layoutMode = 'linear_length';
    this.connectionMode = 'length_to_length';
    this.validationErrors = [];

    // Create default single zone
    this.addZone();
  }

  isWidthAxis() {
    return this.layoutMode === 'linear_width';
  }

  // ─── Zone CRUD ──────────────────────────────────────────────────────────────

  addZone(roofType = 'r-blade') {
    if (this.zones.length >= MAX_ZONES) {
      return { error: `Maximum ${MAX_ZONES} zones allowed` };
    }

    const zone = {
      id: nextId++,
      roofType,
      lengthInches: this.isWidthAxis() ? this.sharedLengthInches : DEFAULT_ZONE.lengthInches,
      widthInches: this.isWidthAxis() ? DEFAULT_ZONE.widthInches : this.sharedWidthInches,
    };
    this.zones.push(zone);
    this._clampZoneDims();
    this.validate();
    return zone;
  }

  removeZone(id) {
    if (this.zones.length <= 1) return false; // min 1 zone
    this.zones = this.zones.filter(z => z.id !== id);
    this.validate();
    return true;
  }

  getZones() {
    return [...this.zones];
  }

  getZone(id) {
    return this.zones.find(z => z.id === id) || null;
  }

  getZoneCount() {
    return this.zones.length;
  }

  updateZone(id, props) {
    const zone = this.zones.find(z => z.id === id);
    if (!zone) return null;

    for (const [key, value] of Object.entries(props)) {
      if (key === 'id') continue;
      if (key in DEFAULT_ZONE || key === 'roofType') {
        zone[key] = value;
      }
    }

    // Enforce per-zone dimension bounds
    if (props.lengthInches !== undefined) {
      zone.lengthInches = Math.max(MIN_ZONE_DIM, Math.min(MAX_ZONE_DIM, zone.lengthInches));
    }
    if (props.widthInches !== undefined) {
      zone.widthInches = Math.max(MIN_ZONE_DIM, Math.min(MAX_ZONE_DIM, zone.widthInches));
    }
    this._clampZoneDims();
    this.validate();
    return zone;
  }

  setSharedWidth(widthInches) {
    this.sharedWidthInches = widthInches;
    if (!this.isWidthAxis()) {
      for (const z of this.zones) z.widthInches = widthInches;
    }
  }

  setSharedLength(lengthInches) {
    this.sharedLengthInches = lengthInches;
    if (this.isWidthAxis()) {
      for (const z of this.zones) z.lengthInches = lengthInches;
    }
  }

  // ─── Layout & Connection Modes ─────────────────────────────────────────────

  setLayoutMode(mode) {
    if (!LAYOUT_MODES[mode]) return false;
    this.layoutMode = mode;
    // Sync shared dimension into zones
    if (this.isWidthAxis()) {
      for (const z of this.zones) z.lengthInches = this.sharedLengthInches;
    } else {
      for (const z of this.zones) z.widthInches = this.sharedWidthInches;
    }
    this.validate();
    return true;
  }

  setConnectionMode(mode) {
    if (!CONNECTION_MODES[mode]) return false;
    this.connectionMode = mode;
    this.validate();
    return true;
  }

  getLayoutMode() { return this.layoutMode; }
  getConnectionMode() { return this.connectionMode; }

  // ─── Validation ────────────────────────────────────────────────────────────

  validate() {
    this.validationErrors = [];

    if (this.zones.length > MAX_ZONES) {
      this.validationErrors.push(`Maximum ${MAX_ZONES} zones allowed (have ${this.zones.length})`);
    }

    const dimLabel = this.isWidthAxis() ? 'width' : 'length';
    for (let i = 0; i < this.zones.length; i++) {
      const z = this.zones[i];
      const dim = this.isWidthAxis() ? z.widthInches : z.lengthInches;
      if (dim < MIN_ZONE_DIM) {
        this.validationErrors.push(`Zone ${i + 1}: ${dimLabel} ${dim}" below minimum ${MIN_ZONE_DIM}"`);
      }
      if (dim > MAX_ZONE_DIM) {
        this.validationErrors.push(`Zone ${i + 1}: ${dimLabel} ${dim}" exceeds maximum ${MAX_ZONE_DIM}"`);
      }
    }

    if (this.layoutMode === 'L_shape' && this.zones.length !== 2) {
      this.validationErrors.push('L-Shape layout requires exactly 2 zones');
    }

    if (this.layoutMode === 'square') {
      const allEqual = this.zones.every(z => z.lengthInches === this.zones[0].lengthInches);
      if (!allEqual) {
        this.validationErrors.push('Square layout requires all zones to have equal length');
      }
    }

    return this.validationErrors;
  }

  getValidationErrors() {
    return [...this.validationErrors];
  }

  isValid() {
    return this.validationErrors.length === 0;
  }

  // ─── Internal: clamp zone dimensions ────────────────────────────────────────

  _clampZoneDims() {
    for (const z of this.zones) {
      z.lengthInches = Math.max(MIN_ZONE_DIM, Math.min(MAX_ZONE_DIM, z.lengthInches));
      z.widthInches = Math.max(MIN_ZONE_DIM, Math.min(MAX_ZONE_DIM, z.widthInches));
    }
  }

  // ─── Spatial Layout ─────────────────────────────────────────────────────────

  getZoneOffsets() {
    if (this.isWidthAxis()) {
      return this._getWidthAxisOffsets();
    }
    return this._getLengthAxisOffsets();
  }

  _getLengthAxisOffsets() {
    const offsets = [];
    const totalLen = this.getTotalLength();
    let runningZ = -totalLen / 2;

    for (const zone of this.zones) {
      const startZ = runningZ;
      const endZ = runningZ + zone.lengthInches;
      offsets.push({
        zoneId: zone.id,
        startZ,
        endZ,
        centerZ: (startZ + endZ) / 2,
        centerX: 0,
        lengthInches: zone.lengthInches,
        widthInches: zone.widthInches,
      });
      runningZ = endZ;
    }
    return offsets;
  }

  _getWidthAxisOffsets() {
    const offsets = [];
    const totalW = this.getTotalWidth();
    let runningX = -totalW / 2;

    for (const zone of this.zones) {
      const startX = runningX;
      const endX = runningX + zone.widthInches;
      offsets.push({
        zoneId: zone.id,
        startX,
        endX,
        centerX: (startX + endX) / 2,
        centerZ: 0,
        lengthInches: zone.lengthInches,
        widthInches: zone.widthInches,
      });
      runningX = endX;
    }
    return offsets;
  }

  // ─── Aggregate Dimensions ───────────────────────────────────────────────────

  getTotalLength() {
    if (this.isWidthAxis()) return this.sharedLengthInches;
    return this.zones.reduce((sum, z) => sum + z.lengthInches, 0);
  }

  getTotalWidth() {
    if (this.isWidthAxis()) return this.zones.reduce((sum, z) => sum + z.widthInches, 0);
    return this.sharedWidthInches;
  }

  // ─── Junction & Structural Calculations ─────────────────────────────────────

  getJunctionCount() {
    return Math.max(0, this.zones.length - 1);
  }

  getJunctionPositionsZ() {
    if (this.isWidthAxis()) return [];
    const positions = [];
    const totalLen = this.getTotalLength();
    let runningZ = -totalLen / 2;

    for (let i = 0; i < this.zones.length - 1; i++) {
      runningZ += this.zones[i].lengthInches;
      positions.push(runningZ);
    }
    return positions;
  }

  getJunctionPositionsX() {
    if (!this.isWidthAxis()) return [];
    const positions = [];
    const totalW = this.getTotalWidth();
    let runningX = -totalW / 2;

    for (let i = 0; i < this.zones.length - 1; i++) {
      runningX += this.zones[i].widthInches;
      positions.push(runningX);
    }
    return positions;
  }

  getScupperCount() {
    return this.zones.length * 2;
  }

  getBeamToBeamCoverCount() {
    return this.getJunctionCount();
  }

  // ─── R-Blade Zones ─────────────────────────────────────────────────────────

  getBladeZones() {
    return this.zones.filter(z => z.roofType === 'r-blade');
  }

  hasBladeZones() {
    return this.zones.some(z => z.roofType === 'r-blade');
  }

  // ─── Summary for Pricing ───────────────────────────────────────────────────

  getSummary() {
    return {
      zoneCount: this.zones.length,
      sharedWidthInches: this.sharedWidthInches,
      sharedLengthInches: this.sharedLengthInches,
      totalLengthInches: this.getTotalLength(),
      totalWidthInches: this.getTotalWidth(),
      junctionCount: this.getJunctionCount(),
      scupperCount: this.getScupperCount(),
      beamToBeamCovers: this.getBeamToBeamCoverCount(),
      layoutMode: this.layoutMode,
      connectionMode: this.connectionMode,
      validationErrors: this.validationErrors,
      zones: this.zones.map(z => ({
        id: z.id,
        roofType: z.roofType,
        lengthInches: z.lengthInches,
        widthInches: z.widthInches,
      })),
    };
  }
}
